import type { LandingConfig, LandingSection } from "../components/SeoLandingPage";

/* 12 rashifal pages, one per sign (item 34), rebuilt 2026-10 after the first
   version shipped as bare text. Each page is now the sign's full reference:
   quick-fact profile tiles (the block snippets lift), the sign's nature,
   career, love, compatibility cards, health, lucky colour/number/gem and a
   nakshatra table, all in the wide card layout the calculator pages use.
   Written in Devanagari for the rashifal query space ("mesh rashifal",
   "मेष राशि का स्वभाव", "मेष राशि का शुभ रंग", "aaj ka singh rashifal").

   A static page cannot honestly carry a dated daily prediction, so every
   page says exactly that and sends the reader to /daily-horoscope and the
   chat for the personal version, instead of faking freshness.

   Adding a sign here is NOT enough to ship it: the path must also go into
   ALLOW (public/index.html), INDEXABLE (IndexPolicy.tsx), reactSnap.include
   (package.json), scripts/prerender.js, scripts/gen-sitemap.js and the
   amplify.yml doc rules, like every prerendered route. */

/* November 2026 transit frame, shared by all 12 monthly sections. Dates
   cross-checked 2026-10-23 across panchang publishers: Guru enters Simha
   31 Oct; Mangal joins 12 Nov; Budh (vakri 24 Oct, Tula) margi 13 Nov;
   Shukra (vakri 3 Oct) margi 14 Nov, enters Tula 22 Nov; Surya enters
   Vrishchik 16 Nov; Devuthani Ekadashi 20 Nov opens vivah season; Shani
   vakri in Meena all month (margi 11 Dec); Rahu-Kumbh/Ketu-Simha until
   5 Dec. No per-sign claim beyond these houses. */
const NOV_CONTEXT =
  "नवंबर 2026 के बड़े गोचर (अपडेटेड 23 अक्टूबर 2026): 12 नवंबर को मंगल सिंह राशि में आता है, जहाँ गुरु 31 अक्टूबर से मौजूद है। 13-14 नवंबर को बुध और शुक्र दोनों मार्गी होते हैं, 16 नवंबर को सूर्य वृश्चिक में प्रवेश करता है, 19 नवंबर की देवउठनी एकादशी से विवाह सीज़न खुलता है, और शनि पूरे महीने मीन राशि में वक्री रहता है (11 दिसंबर को मार्गी)। राहु-केतु की कुंभ-सिंह धुरी का यह आख़िरी पूरा महीना है, 5 दिसंबर को वे राशि बदलते हैं।";

const DISCLAIMER =
  "Aura AI ज्योतिषीय मार्गदर्शन आत्म-चिंतन और समझ के लिए देता है। यह चिकित्सा, क़ानूनी या वित्तीय सलाह नहीं है, और कोई भी बड़ा फ़ैसला सिर्फ़ राशि या कुंडली के आधार पर नहीं लेना चाहिए।";

/* The gemstone caution, shared: the honest line that separates this page
   from the remedy-sellers. */
const GEM_CAUTION =
  "ध्यान रखिए, रत्न सिर्फ़ राशि देखकर नहीं पहना जाता: कुंडली में उस ग्रह की स्थिति देखकर ही तय होता है कि रत्न फ़ायदा करेगा या नहीं। मुफ़्त कुंडली बनाकर AI ज्योतिषी से पूछ लीजिए, पहला सवाल मुफ़्त है।";

/* Element-wise compatibility rationale, one line per card. The sign lists
   come from element friendship and ruler friendship, the classical shortcut;
   every page then says plainly that the real answer is full kundli milan. */
const COMPAT_WHY: Record<string, { good: string; work: string }> = {
  अग्नि: {
    good: "अग्नि और वायु राशियों के साथ ऊर्जा और रफ़्तार का मेल सहज बैठता है।",
    work: "जल और पृथ्वी राशियों के साथ रफ़्तार अलग पड़ती है: रिश्ता चलता है, पर समझदारी माँगता है।",
  },
  पृथ्वी: {
    good: "पृथ्वी और जल राशियों के साथ स्थिरता और भरोसे का मेल सहज बैठता है।",
    work: "अग्नि और वायु राशियों की रफ़्तार जल्दबाज़ी लग सकती है: तालमेल बनता है, पर मेहनत से।",
  },
  वायु: {
    good: "वायु और अग्नि राशियों के साथ विचार और ऊर्जा का मेल सहज बैठता है।",
    work: "पृथ्वी और जल राशियों के साथ ज़रूरतें अलग पड़ती हैं: रिश्ता टिकता है, पर संवाद माँगता है।",
  },
  जल: {
    good: "जल और पृथ्वी राशियों के साथ भावना और स्थिरता का मेल सहज बैठता है।",
    work: "अग्नि और वायु राशियों की सीधी, तेज़ शैली भावुक मन को चुभ सकती है: तालमेल समझदारी से बनता है।",
  },
};

type SignData = {
  path: string;
  hindi: string;
  english: string;
  order: string; // "पहली"
  symbol: string;
  ruler: string;
  rulerShort: string; // for interpolation: "मंगल"
  element: keyof typeof COMPAT_WHY;
  nature: string;
  color: string;
  number: string;
  day: string;
  gem: string;
  bodyPart: string;
  compatGood: string[];
  compatWork: string[];
  naks: [string, string, string][]; // [नक्षत्र, स्वामी, चरण]
  nakAnswer: string; // FAQ answer, names the charans in words
  sub: string;
  lede: [string, string];
  /** The dated monthly rashifal, two paras, refreshed every month in place.
      Current month: November 2026 (written 2026-10-23). */
  november: [string, string];
  nature1: string;
  nature2: string;
  career1: string;
  career2: string;
  love1: string;
  love2: string;
  loveChips: { to: string; label: string }[];
  health1: string;
  health2: string;
  gemPara: string;
  faqRulerA: string;
  schemaDescription: string;
};

const SIGN_DATA: SignData[] = [
  {
    path: "/mesh-rashifal",
    november: [
      "गुरु इस महीने आपके पाँचवें भाव सिंह में है, और 12 नवंबर से मंगल भी वहीं आ जाता है: पढ़ाई, संतान, रचनात्मक काम और प्रेम के मामलों में ऊर्जा साफ़ बढ़ती है। केतु पहले से वहीं है, इसलिए मन कभी-कभी उलझेगा भी; जो शुरू करें, उसे पूरा करने की ज़िद रखिए। शनि आपके बारहवें भाव में वक्री है, यानी साढ़ेसाती का पहला चरण चल रहा है: नींद, ख़र्च और पुराने बोझ संभालकर।",
      "महीने का मोड़ 13-14 नवंबर है, जब बुध और शुक्र मार्गी होते हैं: सातवें भाव में चल रही रिश्ते और साझेदारी की जो बातचीत अटकी थी, वह आगे बढ़ती है। 16 नवंबर से सूर्य आठवें भाव में जाता है, तो किसी के भरोसे पैसा लगाने से पहले काग़ज़ दो बार पढ़िए। 19 के बाद विवाह सीज़न में रिश्ता पक्का करना हो तो कुंडली मिलान पहले कर लीजिए।",
    ],
    hindi: "मेष",
    english: "Aries",
    order: "पहली",
    symbol: "मेढ़ा",
    ruler: "मंगल",
    rulerShort: "मंगल",
    element: "अग्नि",
    nature: "चर",
    color: "लाल",
    number: "9",
    day: "मंगलवार",
    gem: "मूंगा",
    bodyPart: "सिर",
    compatGood: ["सिंह", "धनु", "मिथुन", "कुंभ"],
    compatWork: ["कर्क", "मकर", "तुला"],
    naks: [
      ["अश्विनी", "केतु", "चारों चरण"],
      ["भरणी", "शुक्र", "चारों चरण"],
      ["कृत्तिका", "सूर्य", "प्रथम चरण"],
    ],
    nakAnswer:
      "अश्विनी और भरणी पूरे, और कृत्तिका का पहला चरण। जन्म के समय चंद्रमा इनमें से जिस नक्षत्र में था, वही आपका जन्म नक्षत्र है, और वह राशि से ज़्यादा बारीक जानकारी देता है। मुफ़्त कुंडली में यह अपने आप निकल आता है।",
    sub: "राशि चक्र की पहली राशि, स्वामी मंगल, तत्व अग्नि। मेष का स्वभाव, करियर, प्रेम, शुभ रंग, रत्न और नक्षत्र, एक ही पेज पर।",
    lede: [
      "मेष राशि चक्र की पहली राशि है, और पहली होने का स्वभाव भी रखती है: पहल करना, आगे बढ़कर नेतृत्व लेना, और इंतज़ार से चिढ़ना। स्वामी ग्रह मंगल है, इसलिए ऊर्जा, साहस और सीधापन इस राशि की पहचान है।",
      "यह पेज मेष राशि का स्थायी परिचय है। आज का मेष राशिफल रोज़ ताज़ा चाहिए तो नीचे डेली होरोस्कोप का रास्ता है, और अपनी पूरी कुंडली से जवाब चाहिए तो मुफ़्त कुंडली बनाकर सीधे पूछ सकते हैं।",
    ],
    nature1:
      "मंगल की राशि होने से मेष वालों में ऊर्जा का स्तर ऊँचा रहता है: फ़ैसले तेज़, बात सीधी, और चुनौती से भागने की आदत नहीं। नई शुरुआत इनसे बेहतर कोई नहीं करता, चाहे नया काम हो, नई जगह हो या नया रिश्ता।",
    nature2:
      "कमज़ोर पक्ष भी इसी ऊर्जा से निकलता है: जल्दबाज़ी, ग़ुस्सा और शुरू किए काम को अधूरा छोड़ना। जिस मेष ने धैर्य सीख लिया, उसके लिए मंगल वरदान बन जाता है।",
    career1:
      "मेष को वह काम चाहिए जहाँ पहल और रफ़्तार की क़दर हो: नेतृत्व की भूमिकाएँ, अपना व्यवसाय, सेना-पुलिस, खेल, सर्जरी, इंजीनियरिंग, या कोई भी क्षेत्र जहाँ नतीजा साफ़ दिखता हो। धीमी, दोहराव वाली नौकरी इन्हें थका देती है।",
    career2:
      "पैसे में कमाई अच्छी होती है पर रोकना सीखना पड़ता है: मंगल ख़र्च भी तेज़ करवाता है। बड़े वित्तीय फ़ैसलों में एक रात रुकने की आदत मेष के बहुत काम आती है।",
    love1:
      "प्रेम में मेष पहल करने वाला पक्ष होता है: इज़हार सीधा, भावना सच्ची, और रिश्ते में ऊब बर्दाश्त नहीं। साथी से भी उतनी ही ईमानदारी की उम्मीद रहती है।",
    love2:
      "विवाह में तालमेल का असली इम्तिहान ग़ुस्से और ज़िद का है। कुंडली मिलान में मेष वालों के लिए मंगल की स्थिति ख़ास देखी जाती है, इसलिए सिर्फ़ राशि से नहीं, पूरी कुंडली से मिलान कराना बेहतर है।",
    loveChips: [
      { to: "/kundli-matching", label: "कुंडली मिलान" },
      { to: "/manglik-calculator", label: "मांगलिक दोष जाँच" },
    ],
    health1:
      "काल पुरुष की कुंडली में मेष सिर का स्थान है, इसलिए सिरदर्द, माइग्रेन और गर्मी से जुड़ी शिकायतें परंपरा में इस राशि के खाते में गिनी जाती हैं। मंगल की तेज़ी जल्दबाज़ी में चोट-मोच भी करवाती है।",
    health2:
      "सेहत का सूत्र मेष के लिए ठंडक है: भागदौड़ के बीच नींद पूरी, पानी भरपूर, और तेज़ रफ़्तार में थोड़ा धीरज। ऊर्जा इस राशि की पूँजी है, उसे जलाना नहीं, साधना है।",
    gemPara:
      "परंपरा में मेष का रत्न मूंगा है, जो स्वामी मंगल का रत्न माना जाता है। शुभ रंग लाल, शुभ अंक 9 और शुभ दिन मंगलवार: इसीलिए मेष वालों को मंगलवार के व्रत और हनुमान उपासना की सलाह सबसे पहले दी जाती है।",
    faqRulerA:
      "मेष राशि का स्वामी मंगल है। इसीलिए ऊर्जा, साहस, पहल और सीधापन इस राशि के मूल गुण माने जाते हैं, और कुंडली में मंगल की स्थिति मेष लग्न या मेष राशि वालों के लिए ख़ास मायने रखती है।",
    schemaDescription:
      "Mesh rashi (Aries) profile in Hindi: ruler Mars, fire element, nature, career, love, lucky colour, gemstone and nakshatras, with daily horoscope and free kundli.",
  },
  {
    path: "/vrishabh-rashifal",
    november: [
      "गुरु आपके चौथे भाव में है: घर, गाड़ी, ज़मीन-जायदाद और माँ के सुख का भाव। 12 नवंबर से मंगल भी वहीं आता है, तो घर में मरम्मत, ख़रीद या बदलाव की हलचल बढ़ सकती है; बस दिन की थकान का ग़ुस्सा घर पर न निकले। शनि ग्यारहवें भाव में वक्री है: पुराने दोस्तों या नेटवर्क से रुका हुआ लाभ धीरे-धीरे खुलता है, पीछा करते रहिए।",
      "13-14 नवंबर को बुध और शुक्र मार्गी होते हैं: रोज़मर्रा के काम और सेहत की दिनचर्या पटरी पर लौटती है। 16 से सूर्य सातवें भाव में आता है, जीवनसाथी या पार्टनर से सीधी, साफ़ बात करने का अच्छा समय। आपका स्वामी शुक्र 22 नवंबर को अपनी राशि तुला में लौटता है: शुभ ख़रीदारी और रिश्तों के फ़ैसले महीने की आख़िरी छमाही में बेहतर बैठते हैं।",
    ],
    hindi: "वृषभ",
    english: "Taurus",
    order: "दूसरी",
    symbol: "बैल",
    ruler: "शुक्र",
    rulerShort: "शुक्र",
    element: "पृथ्वी",
    nature: "स्थिर",
    color: "सफ़ेद और गुलाबी",
    number: "6",
    day: "शुक्रवार",
    gem: "हीरा",
    bodyPart: "चेहरा और गला",
    compatGood: ["कन्या", "मकर", "कर्क", "मीन"],
    compatWork: ["सिंह", "कुंभ", "वृश्चिक"],
    naks: [
      ["कृत्तिका", "सूर्य", "अंतिम तीन चरण"],
      ["रोहिणी", "चंद्रमा", "चारों चरण"],
      ["मृगशिरा", "मंगल", "प्रथम दो चरण"],
    ],
    nakAnswer:
      "कृत्तिका के अंतिम तीन चरण, रोहिणी पूरा, और मृगशिरा के पहले दो चरण। जन्म नक्षत्र राशि से ज़्यादा बारीक जानकारी देता है, और मुफ़्त कुंडली में यह अपने आप निकल आता है।",
    sub: "राशि चक्र की दूसरी राशि, स्वामी शुक्र, तत्व पृथ्वी। वृषभ का स्वभाव, करियर, प्रेम, शुभ रंग, रत्न और नक्षत्र, एक ही पेज पर।",
    lede: [
      "वृषभ पृथ्वी तत्व की स्थिर राशि है और स्वामी शुक्र है: इसलिए धैर्य, स्थिरता और सुंदरता की परख इस राशि की पहचान है। वृषभ धीरे चलता है, पर जहाँ पहुँचता है वहाँ टिकता है।",
      "यह पेज वृषभ राशि का स्थायी परिचय है। आज का वृषभ राशिफल रोज़ ताज़ा चाहिए तो नीचे डेली होरोस्कोप का रास्ता है, और अपनी कुंडली से सीधा जवाब चाहिए तो मुफ़्त कुंडली बनाकर पूछ सकते हैं।",
    ],
    nature1:
      "वृषभ वालों को आराम, सुरक्षा और अच्छी चीज़ें पसंद हैं: अच्छा खाना, अच्छा घर, भरोसेमंद लोग। शुक्र की राशि होने से कला और सौंदर्य की समझ जन्मजात होती है। भरोसा धीरे बनता है, पर बन जाए तो पक्का होता है।",
    nature2:
      "कमज़ोर पक्ष ज़िद है: एक बार मन बना लिया तो बदलना मुश्किल, और बदलाव से बेवजह बचना। जो वृषभ समय पर नई चीज़ अपनाना सीख लेता है, उसकी स्थिरता ताक़त बन जाती है, क़ैद नहीं।",
    career1:
      "वृषभ को वे क्षेत्र रास आते हैं जहाँ धैर्य और भरोसा क़ीमती है: बैंकिंग और वित्त, रियल एस्टेट, खेती-बाग़वानी, खान-पान, कला, संगीत और डिज़ाइन। जल्दी-जल्दी दिशा बदलने वाले माहौल में इनका दम घुटता है।",
    career2:
      "पैसा सँभालना इस राशि को क़ुदरती आता है: बचत, संपत्ति और लंबी अवधि का निवेश वृषभ की पकड़ के विषय हैं। ध्यान बस इतना रखना होता है कि सुरक्षा का मोह अच्छे मौक़े न छुड़वा दे।",
    love1:
      "शुक्र की राशि होने से प्रेम वृषभ के जीवन का बड़ा हिस्सा है, पर इज़हार में जल्दी नहीं: पहले भरोसा, फिर भावना। साथ निभाने में यह राशि सबसे आगे गिनी जाती है।",
    love2:
      "विवाह में वृषभ स्थिरता देता भी है और माँगता भी है। झगड़े की जड़ अक्सर ज़िद होती है, बेवफ़ाई नहीं। कुंडली मिलान में भकूट और नाड़ी के साथ शुक्र की स्थिति देखना इस राशि के लिए ख़ास उपयोगी है।",
    loveChips: [
      { to: "/kundli-matching", label: "कुंडली मिलान" },
      { to: "/love-calculator", label: "लव कैलकुलेटर" },
    ],
    health1:
      "काल पुरुष में वृषभ चेहरे, गले और स्वर का स्थान है: गले, थायरॉयड और आवाज़ से जुड़ी शिकायतें परंपरा में इस राशि से जोड़ी जाती हैं। शुक्र की राशि होने से अच्छे खाने का शौक़ वज़न पर भी दिखता है।",
    health2:
      "वृषभ के लिए सेहत का सूत्र चाल है: रोज़ थोड़ा चलना, मीठे पर लगाम, और मौसम बदलते ही गले का ख़याल। आराम इस राशि को प्रिय है, पर आराम ही इलाज नहीं।",
    gemPara:
      "परंपरा में वृषभ का रत्न हीरा है, जो स्वामी शुक्र का रत्न माना जाता है; उपरत्न के रूप में सफ़ेद ओपल भी चलता है। शुभ रंग सफ़ेद और गुलाबी, शुभ अंक 6 और शुभ दिन शुक्रवार है।",
    faqRulerA:
      "वृषभ का स्वामी शुक्र है, इसलिए सौंदर्य, कला, सुख-सुविधा और रिश्तों की गहरी समझ इस राशि के मूल गुण हैं। कुंडली में शुक्र की स्थिति वृषभ वालों के सुख और वैवाहिक जीवन के बारे में बहुत कुछ बताती है।",
    schemaDescription:
      "Vrishabh rashi (Taurus) profile in Hindi: ruler Venus, earth element, nature, career, love, lucky colour, gemstone and nakshatras, with daily horoscope and free kundli.",
  },
  {
    path: "/mithun-rashifal",
    november: [
      "तीसरे भाव का गुरु हिम्मत और पहल देता है, और 12 नवंबर से मंगल भी वहीं: छोटी यात्राएँ, भाई-बहनों के काम और नई शुरुआत के लिए महीना अच्छा है। शनि दसवें भाव में वक्री है: करियर में पुराना काम दोबारा जाँचा जा सकता है, शॉर्टकट मत लीजिए, व्यवस्था से जवाब दीजिए।",
      "आपका स्वामी बुध 13 नवंबर तक वक्री है, इसलिए महीने का पहला हिस्सा बोलने, लिखने और डील में दोहरी जाँच माँगता है; 13 के बाद गाड़ी पकड़ती है। 16 से सूर्य छठे भाव में आता है: रूटीन और सेहत पर ध्यान देने का पखवाड़ा। प्रेम और बच्चों के मामले 22 के बाद नरम पड़ते हैं, जब शुक्र पाँचवें भाव में आ जाता है।",
    ],
    hindi: "मिथुन",
    english: "Gemini",
    order: "तीसरी",
    symbol: "युवा जोड़ा",
    ruler: "बुध",
    rulerShort: "बुध",
    element: "वायु",
    nature: "द्विस्वभाव",
    color: "हरा",
    number: "5",
    day: "बुधवार",
    gem: "पन्ना",
    bodyPart: "कंधे, बाज़ू और फेफड़े",
    compatGood: ["तुला", "कुंभ", "मेष", "सिंह"],
    compatWork: ["कन्या", "मीन", "धनु"],
    naks: [
      ["मृगशिरा", "मंगल", "अंतिम दो चरण"],
      ["आर्द्रा", "राहु", "चारों चरण"],
      ["पुनर्वसु", "गुरु", "प्रथम तीन चरण"],
    ],
    nakAnswer:
      "मृगशिरा के अंतिम दो चरण, आर्द्रा पूरा, और पुनर्वसु के पहले तीन चरण। जन्म नक्षत्र राशि से ज़्यादा बारीक जानकारी देता है, और मुफ़्त कुंडली में यह अपने आप निकल आता है।",
    sub: "राशि चक्र की तीसरी राशि, स्वामी बुध, तत्व वायु। मिथुन का स्वभाव, करियर, प्रेम, शुभ रंग, रत्न और नक्षत्र, एक ही पेज पर।",
    lede: [
      "मिथुन बुध की वायु राशि है: शब्द, तर्क और जिज्ञासा इसकी पहचान हैं। मिथुन वाले बातचीत में सबसे आगे होते हैं, नई जानकारी इन्हें वैसे खींचती है जैसे औरों को आराम।",
      "यह पेज मिथुन राशि का स्थायी परिचय है। आज का मिथुन राशिफल रोज़ ताज़ा चाहिए तो नीचे डेली होरोस्कोप का रास्ता है, और अपनी कुंडली से सीधा जवाब चाहिए तो मुफ़्त कुंडली बनाकर पूछ सकते हैं।",
    ],
    nature1:
      "द्विस्वभाव राशि होने से मिथुन एक साथ कई दिशाओं में सोच सकता है: यही इसकी प्रतिभा है और यही चुनौती। सीखना तेज़, हाज़िरजवाबी ग़ज़ब, और हर उम्र के लोगों से दोस्ती आसान।",
    nature2:
      "कमज़ोर पक्ष बिखराव है: बहुत सारी चीज़ें शुरू, पूरी कम। मन ऊब जल्दी जाता है। जो मिथुन एक-दो विषयों में गहराई बना लेता है, उसके लिए बुध की चंचलता धार बन जाती है।",
    career1:
      "मिथुन के लिए वे क्षेत्र बने हैं जहाँ शब्द और तर्क बिकते हैं: लेखन, पत्रकारिता, मार्केटिंग और सेल्स, शिक्षण, वकालत, मीडिया, टेक्नोलॉजी और हर तरह का संवाद। एक ही काम सालों दोहराना इनके बस की बात नहीं।",
    career2:
      "पैसे में कमाई के रास्ते कई होते हैं, अक्सर एक से ज़्यादा स्रोत। जोखिम यह कि ध्यान बँटा तो पैसा भी बँटता है। एक मुख्य धारा चुनकर बाक़ी को साथ चलाना मिथुन का सही गणित है।",
    love1:
      "मिथुन के लिए प्रेम की शुरुआत बातचीत से होती है: जिससे बात अच्छी, उससे रिश्ता अच्छा। ऐसा साथी चाहिए जो दिमाग़ को भी उतना ही छुए जितना दिल को।",
    love2:
      "विवाह में ऊब इस राशि की सबसे बड़ी परीक्षा है, इसलिए रिश्ते में नयापन बनाए रखना दोनों की ज़िम्मेदारी बन जाती है। कुंडली मिलान में बुध और चंद्रमा की स्थिति मिथुन वालों के लिए ख़ास देखने लायक़ है।",
    loveChips: [
      { to: "/kundli-matching", label: "कुंडली मिलान" },
      { to: "/love-calculator", label: "लव कैलकुलेटर" },
    ],
    health1:
      "मिथुन कंधों, बाज़ुओं और फेफड़ों का स्थान है: साँस, एलर्जी और नसों से जुड़ी शिकायतें परंपरा में इस राशि के हिस्से गिनी जाती हैं। बुध का चंचल मन नींद भी हल्की रखता है।",
    health2:
      "सूत्र है साँस: प्राणायाम या गहरी साँस का कोई भी अभ्यास मिथुन को सबसे ज़्यादा देता है। स्क्रीन और विचारों की भीड़ के बीच मन को रोज़ थोड़ा ख़ाली समय देना इस राशि की असली दवा है।",
    gemPara:
      "परंपरा में मिथुन का रत्न पन्ना है, जो स्वामी बुध का रत्न माना जाता है। शुभ रंग हरा, शुभ अंक 5 और शुभ दिन बुधवार है: बुध बुद्धि और वाणी का कारक है, इसलिए पन्ना पढ़ाई और संवाद से जुड़े कामों के लिए पहना जाता है।",
    faqRulerA:
      "मिथुन का स्वामी बुध है, इसलिए वाणी, बुद्धि, तर्क और व्यापार की समझ इस राशि के मूल गुण हैं। कुंडली में बुध की स्थिति मिथुन वालों की पढ़ाई और करियर के बारे में बहुत कुछ बताती है।",
    schemaDescription:
      "Mithun rashi (Gemini) profile in Hindi: ruler Mercury, air element, nature, career, love, lucky colour, gemstone and nakshatras, with daily horoscope and free kundli.",
  },
  {
    path: "/kark-rashifal",
    november: [
      "दूसरे भाव में गुरु वाणी, परिवार और जमा-पूँजी के लिए अच्छा गोचर है, और 12 नवंबर से मंगल भी वहीं: आमदनी बढ़ाने की मेहनत रंग ला सकती है, बस बोली में तीखापन न आने दें, दूसरा भाव वाणी का भी है। शनि नौवें भाव में वक्री है: इस महीने भाग्य भरोसे नहीं, व्यवस्था भरोसे चलिए।",
      "13-14 नवंबर को बुध और शुक्र मार्गी होते हैं: घर-परिवार के जो फ़ैसले अटके थे, वे खुलते हैं। 16 से सूर्य पाँचवें भाव में आता है: बच्चों, पढ़ाई और रचनात्मक काम पर अच्छा फोकस बनता है। विवाह सीज़न में घर में कोई शुभ काम करना हो तो मुहूर्त पेज से तारीख़ मिलाकर ही पक्का कीजिए।",
    ],
    hindi: "कर्क",
    english: "Cancer",
    order: "चौथी",
    symbol: "केकड़ा",
    ruler: "चंद्रमा",
    rulerShort: "चंद्रमा",
    element: "जल",
    nature: "चर",
    color: "सफ़ेद और चाँदी जैसा",
    number: "2",
    day: "सोमवार",
    gem: "मोती",
    bodyPart: "छाती और पेट",
    compatGood: ["वृश्चिक", "मीन", "वृषभ", "कन्या"],
    compatWork: ["मेष", "तुला", "मकर"],
    naks: [
      ["पुनर्वसु", "गुरु", "चतुर्थ चरण"],
      ["पुष्य", "शनि", "चारों चरण"],
      ["आश्लेषा", "बुध", "चारों चरण"],
    ],
    nakAnswer:
      "पुनर्वसु का चौथा चरण, पुष्य पूरा, और आश्लेषा पूरा। जन्म नक्षत्र राशि से ज़्यादा बारीक जानकारी देता है, और मुफ़्त कुंडली में यह अपने आप निकल आता है।",
    sub: "राशि चक्र की चौथी राशि, स्वामी चंद्रमा, तत्व जल। कर्क का स्वभाव, करियर, प्रेम, शुभ रंग, रत्न और नक्षत्र, एक ही पेज पर।",
    lede: [
      "कर्क चंद्रमा की अपनी राशि है, और चंद्रमा मन का कारक है: इसलिए भावना, स्मृति और अपनों की फ़िक्र इस राशि के केंद्र में है। कर्क वालों के लिए घर सिर्फ़ जगह नहीं, पहचान है।",
      "यह पेज कर्क राशि का स्थायी परिचय है। आज का कर्क राशिफल रोज़ ताज़ा चाहिए तो नीचे डेली होरोस्कोप का रास्ता है, और अपनी कुंडली से सीधा जवाब चाहिए तो मुफ़्त कुंडली बनाकर पूछ सकते हैं।",
    ],
    nature1:
      "कर्क वाले दूसरों की तकलीफ़ पहले भाँप लेते हैं: सहानुभूति इनकी जन्मजात भाषा है। अपनों के लिए यह राशि चट्टान की तरह खड़ी रहती है, और पुरानी बातें, पुराने लोग, पुरानी यादें सँभालकर रखती है।",
    nature2:
      "कमज़ोर पक्ष मूड का उतार-चढ़ाव है: चंद्रमा घटता-बढ़ता है, और कर्क का मन भी। बात चुभ जाए तो भीतर ही भीतर पालने की आदत है। जो कर्क बोलना सीख लेता है, उसके रिश्ते और हल्के हो जाते हैं।",
    career1:
      "कर्क के लिए वे क्षेत्र बने हैं जहाँ देखभाल और भरोसा चाहिए: शिक्षा, चिकित्सा और नर्सिंग, होटल और खान-पान, रियल एस्टेट, मनोविज्ञान, और हर वह काम जहाँ लोगों की ज़िम्मेदारी ली जाती है। टीम में यह राशि परिवार ढूँढती है।",
    career2:
      "पैसे में कर्क बचाने वाला है, दिखावे पर उड़ाने वाला नहीं: सुरक्षा पहली प्राथमिकता है, इसलिए घर और ज़मीन में निवेश इन्हें सबसे सही लगता है। जोखिम से दूरी कभी-कभी अच्छे मौक़े भी दूर कर देती है, यही एक ध्यान रखने की बात है।",
    love1:
      "कर्क का प्रेम गहरा और सुरक्षा माँगने वाला होता है: यह राशि दिल पूरा देती है और बदले में भरोसा पूरा चाहती है। छोटी-सी उपेक्षा भी इन्हें बड़ी लगती है।",
    love2:
      "विवाह और परिवार कर्क के जीवन की धुरी हैं। कुंडली मिलान में चंद्रमा की स्थिति, यानी राशि और नक्षत्र का मेल, कर्क वालों के लिए सबसे ज़्यादा मायने रखता है, क्योंकि पूरा मिलान ही चंद्र कुंडली से होता है।",
    loveChips: [
      { to: "/kundli-matching", label: "कुंडली मिलान" },
      { to: "/moon-sign-calculator", label: "चंद्र राशि कैलकुलेटर" },
    ],
    health1:
      "कर्क छाती और पेट का स्थान है, और चंद्रमा मन का कारक: इसलिए भावना और पाचन का रिश्ता इस राशि में सबसे सीधा है। चिंता सीधी पेट पर उतरती है।",
    health2:
      "सूत्र है नियम: खाने का समय तय, पानी भरपूर, और मन की बात कहने की आदत। जो कर्क भावनाएँ बाँटना सीख लेता है, उसका आधा इलाज वहीं हो जाता है।",
    gemPara:
      "परंपरा में कर्क का रत्न मोती है, जो स्वामी चंद्रमा का रत्न माना जाता है। शुभ रंग सफ़ेद और चाँदी जैसा, शुभ अंक 2 और शुभ दिन सोमवार है: मोती मन की शांति के लिए पहना जाने वाला सबसे जाना-पहचाना रत्न है।",
    faqRulerA:
      "कर्क का स्वामी चंद्रमा है, जो मन, भावना और माता का कारक है। इसीलिए कर्क वालों में संवेदनशीलता और अपनों की फ़िक्र गहरी होती है, और कुंडली में चंद्रमा की स्थिति इनके लिए ख़ास अहमियत रखती है।",
    schemaDescription:
      "Kark rashi (Cancer) profile in Hindi: ruler Moon, water element, nature, career, love, lucky colour, gemstone and nakshatras, with daily horoscope and free kundli.",
  },
  {
    path: "/singh-rashifal",
    november: [
      "गुरु आपकी ही राशि में है और 12 नवंबर से मंगल भी: आत्मविश्वास, पहचान और नई ज़िम्मेदारी का महीना है, जो माँगना था वह माँगने की हिम्मत आती है। केतु भी आपकी राशि में है, इसलिए दिखावे से ज़्यादा काम पर ध्यान रहे। राहु सातवें भाव में है: रिश्ते और पार्टनरशिप में अफ़वाह या ग़लतफ़हमी से सावधान।",
      "शनि आठवें भाव में वक्री है: सेहत की जाँच और काग़ज़ी मामले टालना महँगा पड़ सकता है, समय पर निपटाइए। 13-14 के बाद बातचीत और भागदौड़ आसान होती है। 16 से सूर्य चौथे भाव में आता है: महीने की आख़िरी छमाही में घर को समय दीजिए, वही इस महीने की असली कमाई है।",
    ],
    hindi: "सिंह",
    english: "Leo",
    order: "पाँचवीं",
    symbol: "शेर",
    ruler: "सूर्य",
    rulerShort: "सूर्य",
    element: "अग्नि",
    nature: "स्थिर",
    color: "सुनहरा और नारंगी",
    number: "1",
    day: "रविवार",
    gem: "माणिक",
    bodyPart: "हृदय और पीठ",
    compatGood: ["मेष", "धनु", "मिथुन", "तुला"],
    compatWork: ["वृषभ", "वृश्चिक", "कुंभ"],
    naks: [
      ["मघा", "केतु", "चारों चरण"],
      ["पूर्वा फाल्गुनी", "शुक्र", "चारों चरण"],
      ["उत्तरा फाल्गुनी", "सूर्य", "प्रथम चरण"],
    ],
    nakAnswer:
      "मघा और पूर्वा फाल्गुनी पूरे, और उत्तरा फाल्गुनी का पहला चरण। जन्म नक्षत्र राशि से ज़्यादा बारीक जानकारी देता है, और मुफ़्त कुंडली में यह अपने आप निकल आता है।",
    sub: "राशि चक्र की पाँचवीं राशि, स्वामी सूर्य, तत्व अग्नि। सिंह का स्वभाव, करियर, प्रेम, शुभ रंग, रत्न और नक्षत्र, एक ही पेज पर।",
    lede: [
      "सिंह सूर्य की अपनी राशि है: आत्मविश्वास, गरिमा और नेतृत्व इसका स्वभाव है। जैसे ग्रह सूर्य के चारों ओर घूमते हैं, वैसे ही सिंह वाले जहाँ होते हैं, केंद्र वहीं बन जाता है।",
      "यह पेज सिंह राशि का स्थायी परिचय है। आज का सिंह राशिफल रोज़ ताज़ा चाहिए तो नीचे डेली होरोस्कोप का रास्ता है, और अपनी कुंडली से सीधा जवाब चाहिए तो मुफ़्त कुंडली बनाकर पूछ सकते हैं।",
    ],
    nature1:
      "सिंह वालों में स्वाभिमान और उदारता साथ चलते हैं: यह राशि दिल खोलकर देती है और सम्मान की उतनी ही पक्की उम्मीद रखती है। मुश्किल समय में टीम का हौसला सिंह ही बाँधता है।",
    nature2:
      "कमज़ोर पक्ष अहं है: तारीफ़ की भूख और आलोचना से चिढ़। जो सिंह यह समझ लेता है कि असली गरिमा दिखाने से नहीं, काम से आती है, वह सचमुच का नेता बन जाता है।",
    career1:
      "सिंह के लिए मंच बने हैं: नेतृत्व और प्रबंधन, राजनीति और प्रशासन, अभिनय और कला, खेल, शिक्षण, और हर वह जगह जहाँ ज़िम्मेदारी के साथ पहचान भी मिलती है। परदे के पीछे का काम इस राशि को अधूरा लगता है।",
    career2:
      "पैसे में सिंह कमाता शान से है और ख़र्च भी शान से करता है: दिखावे पर ख़र्च इस राशि की जानी-पहचानी कमज़ोरी है। आमदनी का एक हिस्सा अपने आप बचत में जाए, ऐसा इंतज़ाम सिंह के लिए सबसे कारगर रहता है।",
    love1:
      "सिंह का प्रेम भव्य होता है: इज़हार खुलकर, वफ़ादारी पूरी, और साथी पर गर्व। बदले में चाहिए ध्यान और क़द्र, उपेक्षा सिंह से बर्दाश्त नहीं होती।",
    love2:
      "विवाह में टकराव तब आता है जब दोनों में से कोई झुकना नहीं जानता। कुंडली मिलान के साथ-साथ सूर्य और मंगल की स्थिति देखना सिंह वालों के लिए उपयोगी है, ताकि अहं का टकराव पहले दिख जाए।",
    loveChips: [
      { to: "/kundli-matching", label: "कुंडली मिलान" },
      { to: "/manglik-calculator", label: "मांगलिक दोष जाँच" },
    ],
    health1:
      "सिंह हृदय और पीठ का स्थान है: दिल, रीढ़ और रक्तचाप परंपरा में इस राशि के देखने के विषय माने जाते हैं। काम और ज़िम्मेदारी का बोझ सिंह ख़ुद पर ज़्यादा लेता है।",
    health2:
      "सूत्र है लय: काम जितना भी बड़ा हो, नींद और सैर की लय न टूटे। दिल की सेहत के लिए नियमित जाँच, और ग़ुस्से पर हँसी, दोनों सिंह के काम आते हैं।",
    gemPara:
      "परंपरा में सिंह का रत्न माणिक है, जो स्वामी सूर्य का रत्न माना जाता है। शुभ रंग सुनहरा और नारंगी, शुभ अंक 1 और शुभ दिन रविवार है: सूर्य आत्मविश्वास और अधिकार का कारक है, इसीलिए माणिक नेतृत्व के लिए पहना जाता है।",
    faqRulerA:
      "सिंह का स्वामी सूर्य है, जो आत्मा, पिता और अधिकार का कारक है। इसीलिए नेतृत्व और स्वाभिमान इस राशि के मूल गुण हैं, और कुंडली में सूर्य की स्थिति सिंह वालों के करियर के बारे में बहुत कुछ बताती है।",
    schemaDescription:
      "Singh rashi (Leo) profile in Hindi: ruler Sun, fire element, nature, career, love, lucky colour, gemstone and nakshatras, with daily horoscope and free kundli.",
  },
  {
    path: "/kanya-rashifal",
    november: [
      "गुरु बारहवें भाव में है: ख़र्च बढ़ेगा, पर सीखने, यात्रा या सेवा जैसी सही जगह लगे तो बोझ नहीं लगेगा। 12 नवंबर से मंगल भी वहीं: बाहर या विदेश से जुड़ा काम हिलता है। शनि सातवें भाव में वक्री है: साझेदारी में कोई पुरानी बात फिर उठे तो धैर्य रखिए, फ़ैसला भावना में नहीं।",
      "शुक्र महीने के पहले हिस्से में आपकी ही राशि में वक्री है और 14 नवंबर को यहीं मार्गी होता है: रूप, रिश्ते और पैसे के मामले मध्य नवंबर से साफ़ दिखने लगते हैं। आपका स्वामी बुध भी 13 को मार्गी: हिसाब-किताब और डील दूसरी छमाही में बेहतर। 16 से सूर्य तीसरे भाव में: हिम्मत से रखी बात सुनी जाएगी।",
    ],
    hindi: "कन्या",
    english: "Virgo",
    order: "छठी",
    symbol: "कन्या",
    ruler: "बुध",
    rulerShort: "बुध",
    element: "पृथ्वी",
    nature: "द्विस्वभाव",
    color: "हरा",
    number: "5",
    day: "बुधवार",
    gem: "पन्ना",
    bodyPart: "आँतें और पाचन तंत्र",
    compatGood: ["वृषभ", "मकर", "कर्क", "वृश्चिक"],
    compatWork: ["मिथुन", "धनु", "मीन"],
    naks: [
      ["उत्तरा फाल्गुनी", "सूर्य", "अंतिम तीन चरण"],
      ["हस्त", "चंद्रमा", "चारों चरण"],
      ["चित्रा", "मंगल", "प्रथम दो चरण"],
    ],
    nakAnswer:
      "उत्तरा फाल्गुनी के अंतिम तीन चरण, हस्त पूरा, और चित्रा के पहले दो चरण। जन्म नक्षत्र राशि से ज़्यादा बारीक जानकारी देता है, और मुफ़्त कुंडली में यह अपने आप निकल आता है।",
    sub: "राशि चक्र की छठी राशि, स्वामी बुध, तत्व पृथ्वी। कन्या का स्वभाव, करियर, प्रेम, शुभ रंग, रत्न और नक्षत्र, एक ही पेज पर।",
    lede: [
      "कन्या बुध की पृथ्वी राशि है: बुद्धि यहाँ हवा में नहीं उड़ती, ज़मीन पर काम करती है। बारीकी, व्यवस्था और सुधार की नज़र कन्या की पहचान है, जो ग़लती सबसे पहले इन्हें दिखती है।",
      "यह पेज कन्या राशि का स्थायी परिचय है। आज का कन्या राशिफल रोज़ ताज़ा चाहिए तो नीचे डेली होरोस्कोप का रास्ता है, और अपनी कुंडली से सीधा जवाब चाहिए तो मुफ़्त कुंडली बनाकर पूछ सकते हैं।",
    ],
    nature1:
      "कन्या वाले काम को पूरा और सही करना जानते हैं: आधा-अधूरा इनसे होता ही नहीं। सेवा-भाव गहरा है, मदद माँगने से पहले मिल जाती है, और भरोसे के मामले में यह राशि सोने जैसी है।",
    nature2:
      "कमज़ोर पक्ष अति-विश्लेषण है: अपनी और दूसरों की ग़लतियों पर ज़रूरत से ज़्यादा सोचना, और चिंता पालना। जो कन्या 'काफ़ी अच्छा' स्वीकारना सीख लेती है, उसकी बारीक नज़र वरदान बन जाती है।",
    career1:
      "कन्या के लिए वे क्षेत्र बने हैं जहाँ बारीकी ही काम है: चिकित्सा और फ़ार्मेसी, लेखा और ऑडिट, विश्लेषण और रिसर्च, संपादन, गुणवत्ता जाँच, और टेक्नोलॉजी। लापरवाह माहौल में इनका दम घुटता है।",
    career2:
      "पैसे का हिसाब इस राशि से बेहतर कोई नहीं रखता: बजट, बचत और सोच-समझकर निवेश। जोखिम बस इतना कि ज़्यादा सावधानी कभी-कभी बड़े मौक़े रोक देती है।",
    love1:
      "कन्या का प्रेम शब्दों से कम, कामों से ज़्यादा दिखता है: वक़्त पर दवा याद रखना, मुश्किल दिन में चुपचाप खड़े रहना। इज़हार में झिझक है, निभाने में नहीं।",
    love2:
      "विवाह में टोकने की आदत सबसे बड़ा काँटा बन सकती है: सुधार की नीयत अच्छी है, पर साथी को आलोचना लगती है। कुंडली मिलान के साथ यह समझ लेना कि क़द्र जताना भी उतना ही ज़रूरी है, कन्या के रिश्ते को मज़बूत करता है।",
    loveChips: [
      { to: "/kundli-matching", label: "कुंडली मिलान" },
      { to: "/love-calculator", label: "लव कैलकुलेटर" },
    ],
    health1:
      "कन्या आँतों और पाचन तंत्र का स्थान है: पेट इस राशि का बैरोमीटर है, और चिंता सबसे पहले पाचन बिगाड़ती है।",
    health2:
      "सूत्र है सादगी: सादा खाना, तय समय, और सोचने के लिए काग़ज़। जो बात दिमाग़ में घूम रही है उसे लिख देना कन्या के लिए अचूक नुस्ख़ा है, पेट और नींद दोनों के लिए।",
    gemPara:
      "परंपरा में कन्या का रत्न पन्ना है, जो स्वामी बुध का रत्न माना जाता है। शुभ रंग हरा, शुभ अंक 5 और शुभ दिन बुधवार है: बुध की दोनों राशियों, मिथुन और कन्या, का रत्न एक ही है, पर कुंडली देखे बिना दोनों में से किसी को नहीं पहनना चाहिए।",
    faqRulerA:
      "कन्या का स्वामी बुध है, और यहाँ बुध पृथ्वी तत्व में है, इसलिए बुद्धि व्यावहारिक रूप लेती है: विश्लेषण, व्यवस्था और सेवा। कुंडली में बुध की स्थिति कन्या वालों की सेहत और काम के बारे में बहुत कुछ बताती है।",
    schemaDescription:
      "Kanya rashi (Virgo) profile in Hindi: ruler Mercury, earth element, nature, career, love, lucky colour, gemstone and nakshatras, with daily horoscope and free kundli.",
  },
  {
    path: "/tula-rashifal",
    november: [
      "गुरु ग्यारहवें भाव में है: लाभ, नेटवर्क और बड़ी इच्छाओं का भाव, और 12 नवंबर से मंगल भी वहीं: आमदनी के नए रास्ते पर काम तेज़ होता है। महीने की शुरुआत में सूर्य और वक्री बुध आपकी ही राशि में हैं: ध्यान आप पर रहेगा, पर 13 तक बड़े एलान और नए क़रार टालिए।",
      "असली मोड़ 22 नवंबर है: आपका स्वामी शुक्र मार्गी होकर आपकी राशि में लौटता है, ठीक विवाह सीज़न के बीच। रिश्ते, समझौते और ख़रीदारी, सबके लिए महीने की दूसरी छमाही पहली से बेहतर है। शनि छठे भाव में वक्री है: सेहत की जो पुरानी आदत छोड़ी थी, उसे वापस मत पकड़िए।",
    ],
    hindi: "तुला",
    english: "Libra",
    order: "सातवीं",
    symbol: "तराज़ू",
    ruler: "शुक्र",
    rulerShort: "शुक्र",
    element: "वायु",
    nature: "चर",
    color: "सफ़ेद और हल्का नीला",
    number: "6",
    day: "शुक्रवार",
    gem: "हीरा",
    bodyPart: "कमर और गुर्दे",
    compatGood: ["मिथुन", "कुंभ", "सिंह", "धनु"],
    compatWork: ["कर्क", "मकर", "मेष"],
    naks: [
      ["चित्रा", "मंगल", "अंतिम दो चरण"],
      ["स्वाति", "राहु", "चारों चरण"],
      ["विशाखा", "गुरु", "प्रथम तीन चरण"],
    ],
    nakAnswer:
      "चित्रा के अंतिम दो चरण, स्वाति पूरा, और विशाखा के पहले तीन चरण। जन्म नक्षत्र राशि से ज़्यादा बारीक जानकारी देता है, और मुफ़्त कुंडली में यह अपने आप निकल आता है।",
    sub: "राशि चक्र की सातवीं राशि, स्वामी शुक्र, तत्व वायु। तुला का स्वभाव, करियर, प्रेम, शुभ रंग, रत्न और नक्षत्र, एक ही पेज पर।",
    lede: [
      "तुला का चिह्न ही तराज़ू है: संतुलन, न्याय और सबको साथ लेकर चलना इस राशि का स्वभाव है। शुक्र की वायु राशि होने से सौंदर्य की परख और लोगों से जुड़ने की कला, दोनों जन्मजात हैं।",
      "यह पेज तुला राशि का स्थायी परिचय है। आज का तुला राशिफल रोज़ ताज़ा चाहिए तो नीचे डेली होरोस्कोप का रास्ता है, और अपनी कुंडली से सीधा जवाब चाहिए तो मुफ़्त कुंडली बनाकर पूछ सकते हैं।",
    ],
    nature1:
      "तुला वाले कमरे का तापमान बदल देते हैं: झगड़े में बीच-बचाव, बातचीत में शिष्टता, और हर पक्ष को सुनने का धैर्य। अन्याय इन्हें निजी तौर पर चुभता है, चाहे किसी और के साथ हो रहा हो।",
    nature2:
      "कमज़ोर पक्ष निर्णय की देर है: हर पलड़ा तौलते-तौलते मौक़ा निकल जाता है, और सबको ख़ुश रखने की कोशिश में अपनी बात दब जाती है। जो तुला 'न' कहना सीख लेता है, उसका संतुलन ताक़त बन जाता है।",
    career1:
      "तुला के लिए वे क्षेत्र बने हैं जहाँ संतुलन और सौंदर्य दोनों चाहिए: वकालत और न्याय, कूटनीति, डिज़ाइन और फ़ैशन, कला, मानव संसाधन, और साझेदारी के व्यवसाय। अकेले काम करने से बेहतर यह राशि जोड़ी में चमकती है।",
    career2:
      "पैसे में तुला को अच्छी चीज़ों का शौक़ है, इसलिए ख़र्च सुंदरता पर सहज बहता है। आमदनी अक्सर साझेदारी और नेटवर्क से बढ़ती है, और बचत के लिए अपने आप कटने वाला इंतज़ाम सबसे कारगर रहता है।",
    love1:
      "तुला प्रेम के लिए बनी राशि कही जाती है: सातवाँ भाव, यानी साथी का भाव, राशि चक्र में तुला का ही है। रिश्ते में यह राशि बराबरी चाहती है, मालिक या नौकर नहीं, साथी।",
    love2:
      "विवाह में तुला निभाने वाला पक्ष है, पर टकराव टालने की आदत कभी-कभी बात को भीतर सड़ने देती है। कुंडली मिलान के साथ शुक्र की स्थिति देखना तुला वालों के लिए ख़ास उपयोगी है।",
    loveChips: [
      { to: "/kundli-matching", label: "कुंडली मिलान" },
      { to: "/love-calculator", label: "लव कैलकुलेटर" },
    ],
    health1:
      "तुला कमर और गुर्दों का स्थान है: कमर दर्द, त्वचा और पानी की कमी से जुड़ी शिकायतें परंपरा में इस राशि के हिस्से आती हैं।",
    health2:
      "सूत्र है संतुलन, जो इस राशि का स्वभाव भी है: पानी भरपूर, बैठने की मुद्रा सीधी, और मीठे-तले पर शुक्र के शौक़ की लगाम। रिश्तों की खींचतान तुला की नींद सबसे पहले छीनती है, इसलिए मन का संतुलन ही असली दवा है।",
    gemPara:
      "परंपरा में तुला का रत्न हीरा है, जो स्वामी शुक्र का रत्न माना जाता है; उपरत्न के रूप में सफ़ेद ओपल चलता है। शुभ रंग सफ़ेद और हल्का नीला, शुभ अंक 6 और शुभ दिन शुक्रवार है।",
    faqRulerA:
      "तुला का स्वामी शुक्र है, जो प्रेम, कला और साझेदारी का कारक है। इसीलिए रिश्ते और संतुलन इस राशि के केंद्र में हैं, और कुंडली में शुक्र की स्थिति तुला वालों के वैवाहिक जीवन के बारे में बहुत कुछ बताती है।",
    schemaDescription:
      "Tula rashi (Libra) profile in Hindi: ruler Venus, air element, nature, career, love, lucky colour, gemstone and nakshatras, with daily horoscope and free kundli.",
  },
  {
    path: "/vrishchik-rashifal",
    november: [
      "गुरु दसवें भाव में है और 12 नवंबर से आपका स्वामी मंगल भी वहीं: करियर के लिए इस महीने का सबसे मज़बूत गोचर आपके पास है, ज़िम्मेदारी बढ़े तो लीजिए, दिखने का समय है। शनि पाँचवें भाव में वक्री है: बच्चों या पढ़ाई का कोई पुराना विषय फिर खुलेगा, चिढ़ से नहीं, व्यवस्था से सुलझाइए।",
      "16 नवंबर से सूर्य आपकी राशि में आता है: जन्मदिन का महीना शुरू होता है, ऊर्जा और पहचान दोनों लौटती हैं। उससे पहले का पखवाड़ा समेटने और आराम का रखिए। 13-14 के बाद रुका पैसा और अटकी बातचीत खुलती है; विवाह सीज़न की बात चले तो कुंडली मिलान पहले।",
    ],
    hindi: "वृश्चिक",
    english: "Scorpio",
    order: "आठवीं",
    symbol: "बिच्छू",
    ruler: "मंगल",
    rulerShort: "मंगल",
    element: "जल",
    nature: "स्थिर",
    color: "लाल और मैरून",
    number: "9",
    day: "मंगलवार",
    gem: "मूंगा",
    bodyPart: "प्रजनन तंत्र",
    compatGood: ["कर्क", "मीन", "कन्या", "मकर"],
    compatWork: ["सिंह", "कुंभ", "वृषभ"],
    naks: [
      ["विशाखा", "गुरु", "चतुर्थ चरण"],
      ["अनुराधा", "शनि", "चारों चरण"],
      ["ज्येष्ठा", "बुध", "चारों चरण"],
    ],
    nakAnswer:
      "विशाखा का चौथा चरण, अनुराधा पूरा, और ज्येष्ठा पूरा। जन्म नक्षत्र राशि से ज़्यादा बारीक जानकारी देता है, और मुफ़्त कुंडली में यह अपने आप निकल आता है।",
    sub: "राशि चक्र की आठवीं राशि, स्वामी मंगल, तत्व जल। वृश्चिक का स्वभाव, करियर, प्रेम, शुभ रंग, रत्न और नक्षत्र, एक ही पेज पर।",
    lede: [
      "वृश्चिक गहराई की राशि है: मंगल की ऊर्जा यहाँ जल तत्व में उतरती है, इसलिए जो भी करती है, पूरी शिद्दत से करती है। सतही बातें, सतही लोग और सतही रिश्ते इस राशि को रास नहीं आते।",
      "यह पेज वृश्चिक राशि का स्थायी परिचय है। आज का वृश्चिक राशिफल रोज़ ताज़ा चाहिए तो नीचे डेली होरोस्कोप का रास्ता है, और अपनी कुंडली से सीधा जवाब चाहिए तो मुफ़्त कुंडली बनाकर पूछ सकते हैं।",
    ],
    nature1:
      "वृश्चिक वालों की इच्छाशक्ति राशि चक्र में सबसे पक्की मानी जाती है: जो ठान लिया, वह होकर रहेगा। लोगों को पढ़ने की नज़र इतनी गहरी होती है कि झूठ इनसे छिपता नहीं, और राज़ इनके पास तिजोरी से ज़्यादा सुरक्षित रहता है।",
    nature2:
      "कमज़ोर पक्ष भी गहराई से निकलता है: बातें भीतर रखना, पुरानी चोट न भूलना, और शक। जो वृश्चिक माफ़ करना सीख लेता है, वह राख से फिर खड़े होने की अपनी ताक़त का पूरा इस्तेमाल कर पाता है।",
    career1:
      "वृश्चिक के लिए वे क्षेत्र बने हैं जहाँ गहराई में उतरना पड़ता है: रिसर्च और जाँच, चिकित्सा और सर्जरी, मनोविज्ञान, क़ानून, गुप्त सूचना और सुरक्षा, और संकट प्रबंधन। जहाँ और लोग घबराकर छोड़ देते हैं, वृश्चिक वहीं से शुरू करता है।",
    career2:
      "पैसे में यह राशि लंबा खेल खेलती है: दिखावा कम, नियंत्रण पूरा। दूसरों के धन का प्रबंधन, बीमा, टैक्स जैसे विषय भी इसी राशि के खाते में आते हैं। जोखिम लेने से पहले पूरी पड़ताल वृश्चिक की आदत भी है और सलाह भी।",
    love1:
      "वृश्चिक का प्रेम आधा-अधूरा नहीं होता: या पूरा या बिल्कुल नहीं। वफ़ादारी गहरी है और उम्मीद भी उतनी ही: विश्वासघात इस राशि के लिए रिश्ते की मौत है।",
    love2:
      "विवाह में शक और क़ब्ज़े की भावना सबसे बड़ी परीक्षा है। वृश्चिक मंगल की राशि है, इसलिए कुंडली मिलान में मंगल दोष की जाँच इनके लिए ख़ास ज़रूरी मानी जाती है।",
    loveChips: [
      { to: "/kundli-matching", label: "कुंडली मिलान" },
      { to: "/manglik-calculator", label: "मांगलिक दोष जाँच" },
    ],
    health1:
      "वृश्चिक प्रजनन तंत्र का स्थान है, और जल तत्व की यह राशि भीतर बहुत कुछ दबाकर रखती है: दबा तनाव ही इस राशि की सबसे आम शिकायत की जड़ है।",
    health2:
      "सूत्र है निकास: पसीना बहाने वाला व्यायाम, और भरोसे का एक रिश्ता जहाँ मन खोला जा सके। वृश्चिक जितना बाहर निकालना सीखता है, भीतर उतना हल्का होता है।",
    gemPara:
      "परंपरा में वृश्चिक का रत्न मूंगा है, जो स्वामी मंगल का रत्न माना जाता है। शुभ रंग लाल और मैरून, शुभ अंक 9 और शुभ दिन मंगलवार है: मंगल की दोनों राशियों, मेष और वृश्चिक, का रत्न एक ही है।",
    faqRulerA:
      "वृश्चिक का स्वामी मंगल है, पर यहाँ मंगल की ऊर्जा जल तत्व में है, इसलिए यह बाहर की आक्रामकता से ज़्यादा भीतर की इच्छाशक्ति बनती है। कुंडली में मंगल की स्थिति वृश्चिक वालों के लिए ख़ास मायने रखती है।",
    schemaDescription:
      "Vrishchik rashi (Scorpio) profile in Hindi: ruler Mars, water element, nature, career, love, lucky colour, gemstone and nakshatras, with daily horoscope and free kundli.",
  },
  {
    path: "/dhanu-rashifal",
    november: [
      "आपका स्वामी गुरु नौवें भाव सिंह में है: भाग्य, धर्म, यात्रा और गुरुजनों का साथ, धनु के लिए यह गोचर घर जैसा बैठता है। 12 नवंबर से मंगल भी वहीं: लंबी यात्रा, तीर्थ या किसी बड़े फ़ैसले की हिम्मत बनती है। शनि चौथे भाव में वक्री है: घर या प्रॉपर्टी का पुराना मसला जल्दबाज़ी नहीं, धीरज माँगता है।",
      "13-14 नवंबर के बाद लाभ के रास्ते साफ़ होते हैं, रुकी हुई आमदनी की बात आगे बढ़ती है। 16 से सूर्य बारहवें भाव में जाता है: महीने का आख़िरी हिस्सा ख़र्च और विश्राम का है, उसे बुरा मत मानिए, अगले महीने की तैयारी मानिए। विवाह सीज़न की तारीख़ें देखनी हों तो शुभ मुहूर्त पेज पर हैं।",
    ],
    hindi: "धनु",
    english: "Sagittarius",
    order: "नौवीं",
    symbol: "धनुर्धर",
    ruler: "गुरु (बृहस्पति)",
    rulerShort: "गुरु",
    element: "अग्नि",
    nature: "द्विस्वभाव",
    color: "पीला",
    number: "3",
    day: "गुरुवार",
    gem: "पुखराज",
    bodyPart: "जाँघें और कूल्हे",
    compatGood: ["मेष", "सिंह", "तुला", "कुंभ"],
    compatWork: ["कन्या", "मीन", "मिथुन"],
    naks: [
      ["मूल", "केतु", "चारों चरण"],
      ["पूर्वाषाढ़ा", "शुक्र", "चारों चरण"],
      ["उत्तराषाढ़ा", "सूर्य", "प्रथम चरण"],
    ],
    nakAnswer:
      "मूल और पूर्वाषाढ़ा पूरे, और उत्तराषाढ़ा का पहला चरण। जन्म नक्षत्र राशि से ज़्यादा बारीक जानकारी देता है, और मुफ़्त कुंडली में यह अपने आप निकल आता है।",
    sub: "राशि चक्र की नौवीं राशि, स्वामी गुरु, तत्व अग्नि। धनु का स्वभाव, करियर, प्रेम, शुभ रंग, रत्न और नक्षत्र, एक ही पेज पर।",
    lede: [
      "धनु गुरु की अग्नि राशि है: ज्ञान की भूख और सच बोलने की हिम्मत इसकी पहचान है। धनु का तीर हमेशा दूर देखता है, बड़े सपने, बड़े सवाल, बड़ी यात्राएँ।",
      "यह पेज धनु राशि का स्थायी परिचय है। आज का धनु राशिफल रोज़ ताज़ा चाहिए तो नीचे डेली होरोस्कोप का रास्ता है, और अपनी कुंडली से सीधा जवाब चाहिए तो मुफ़्त कुंडली बनाकर पूछ सकते हैं।",
    ],
    nature1:
      "धनु वाले आशावादी होते हैं: गिरकर उठना और फिर हँस देना इन्हें आता है। दर्शन, धर्म, क़ानून और दूर देशों में गहरी रुचि गुरु की देन है, और इनकी संगत में लोग अपने आप बेहतर सोचने लगते हैं।",
    nature2:
      "कमज़ोर पक्ष वही सीधापन है: सच इतना खरा बोलते हैं कि कभी-कभी चोट कर जाता है, और वादे उत्साह में ज़्यादा हो जाते हैं। जो धनु शब्द तौलना सीख लेता है, उसकी बात का वज़न दोगुना हो जाता है।",
    career1:
      "धनु के लिए वे क्षेत्र बने हैं जहाँ ज्ञान बाँटा जाता है: शिक्षण और प्रोफ़ेसरी, क़ानून और न्याय, लेखन-प्रकाशन, यात्रा और विदेश से जुड़ा काम, धर्म-दर्शन, और सलाहकारी। बंद कमरे का दोहराव वाला काम इस राशि का दम घोंटता है।",
    career2:
      "पैसे में धनु उदार है, कभी-कभी ज़रूरत से ज़्यादा: देने में आगे, हिसाब में पीछे। क़िस्मत का साथ इस राशि को गुरु की कृपा से मिलता माना जाता है, पर बचत का अनुशासन अपने हाथ से ही बनता है।",
    love1:
      "धनु का प्रेम खुली हवा जैसा है: साथ चाहिए पर जकड़न नहीं। ऐसा साथी चाहिए जो सफ़र में, सोच में और हँसी में बराबर चले। ईमानदारी यह राशि ख़ुद भी रखती है और माँगती भी है।",
    love2:
      "विवाह में आज़ादी और ज़िम्मेदारी का संतुलन ही असली सवाल है। कुंडली मिलान में गुरु की स्थिति, जो विवाह और संतान दोनों का कारक है, धनु वालों के लिए ख़ास देखने लायक़ है।",
    loveChips: [
      { to: "/kundli-matching", label: "कुंडली मिलान" },
      { to: "/love-calculator", label: "लव कैलकुलेटर" },
    ],
    health1:
      "धनु जाँघों और कूल्हों का स्थान है, और गुरु यकृत यानी लिवर का कारक: खाने-पीने की उदारता सीधे वहीं दिखती है। यात्राओं की राशि होने से चोट-मोच भी सफ़र में ही ज़्यादा होती है।",
    health2:
      "सूत्र है माप: खाने में, मेहमाननवाज़ी में और भरोसे में भी। रोज़ की सैर धनु को वैसे भी प्रिय है, बस उसे नियम बना लेना काफ़ी है।",
    gemPara:
      "परंपरा में धनु का रत्न पुखराज है, जो स्वामी गुरु का रत्न माना जाता है। शुभ रंग पीला, शुभ अंक 3 और शुभ दिन गुरुवार है: गुरु ज्ञान और भाग्य का कारक है, इसीलिए पुखराज सबसे ज़्यादा माँगा जाने वाला रत्न है।",
    faqRulerA:
      "धनु का स्वामी गुरु (बृहस्पति) है, जो ज्ञान, धर्म, भाग्य और विस्तार का कारक है। इसीलिए सीखना-सिखाना और सच बोलना इस राशि के मूल गुण हैं, और कुंडली में गुरु की स्थिति धनु वालों के भाग्य के बारे में बहुत कुछ बताती है।",
    schemaDescription:
      "Dhanu rashi (Sagittarius) profile in Hindi: ruler Jupiter, fire element, nature, career, love, lucky colour, gemstone and nakshatras, with daily horoscope and free kundli.",
  },
  {
    path: "/makar-rashifal",
    november: [
      "गुरु आठवें भाव में है: गहराई का महीना, रिसर्च, बीमा, टैक्स और हिसाब की सफ़ाई के लिए अच्छा। 12 नवंबर से मंगल भी वहीं: अचानक ख़र्च या मरम्मत आ सकती है, इमरजेंसी फ़ंड छूकर रखिए। आपका स्वामी शनि तीसरे भाव में वक्री है: हिम्मत है, पर रफ़्तार नापकर बढ़ाइए।",
      "करियर में 13 नवंबर तक के वादे हल्के रखिए, 13-14 के बाद बात पक्की कीजिए। 16 से सूर्य ग्यारहवें भाव में आता है: सीनियर्स से लाभ और पहचान का अच्छा पखवाड़ा। 5 दिसंबर से राहु आपकी राशि में आने वाला है: नवंबर को उसकी तैयारी का महीना मानिए, काग़ज़, सेहत और रूटीन दुरुस्त कर लीजिए।",
    ],
    hindi: "मकर",
    english: "Capricorn",
    order: "दसवीं",
    symbol: "मगर",
    ruler: "शनि",
    rulerShort: "शनि",
    element: "पृथ्वी",
    nature: "चर",
    color: "गहरा नीला और काला",
    number: "8",
    day: "शनिवार",
    gem: "नीलम",
    bodyPart: "घुटने, हड्डियाँ और जोड़",
    compatGood: ["वृषभ", "कन्या", "वृश्चिक", "मीन"],
    compatWork: ["मेष", "तुला", "कर्क"],
    naks: [
      ["उत्तराषाढ़ा", "सूर्य", "अंतिम तीन चरण"],
      ["श्रवण", "चंद्रमा", "चारों चरण"],
      ["धनिष्ठा", "मंगल", "प्रथम दो चरण"],
    ],
    nakAnswer:
      "उत्तराषाढ़ा के अंतिम तीन चरण, श्रवण पूरा, और धनिष्ठा के पहले दो चरण। जन्म नक्षत्र राशि से ज़्यादा बारीक जानकारी देता है, और मुफ़्त कुंडली में यह अपने आप निकल आता है।",
    sub: "राशि चक्र की दसवीं राशि, स्वामी शनि, तत्व पृथ्वी। मकर का स्वभाव, करियर, प्रेम, शुभ रंग, रत्न और नक्षत्र, एक ही पेज पर।",
    lede: [
      "मकर शनि की पृथ्वी राशि है: मेहनत, अनुशासन और ज़िम्मेदारी इसकी भाषा है। मकर का रास्ता लंबा होता है पर चढ़ाई पक्की: पहाड़ धीरे चढ़ा जाता है, और यह राशि चोटी तक पहुँचती है।",
      "यह पेज मकर राशि का स्थायी परिचय है। आज का मकर राशिफल रोज़ ताज़ा चाहिए तो नीचे डेली होरोस्कोप का रास्ता है, और शनि से जुड़ी साढ़े साती की जाँच भी नीचे है।",
    ],
    nature1:
      "मकर वाले कम बोलते हैं, ज़्यादा करते हैं: ज़िम्मेदारी इन्हें बोझ नहीं, पहचान लगती है। उम्र से पहले परिपक्वता आ जाती है, और मुश्किल समय में परिवार और टीम का भार यही राशि चुपचाप उठाती है।",
    nature2:
      "कमज़ोर पक्ष सख़्ती है: ख़ुद पर इतने कठोर कि आराम भी अपराध लगे, और भावना जताने में कंजूस। जो मकर रुकना और जीना सीख लेता है, उसकी मेहनत का फल दुगना मीठा हो जाता है।",
    career1:
      "मकर करियर की ही राशि है: दसवाँ भाव, यानी कर्म का भाव, राशि चक्र में मकर का है। प्रबंधन और प्रशासन, सरकारी सेवा, इंजीनियरिंग और निर्माण, वित्त, और हर वह क्षेत्र जहाँ धैर्य और ढाँचा चाहिए, इस राशि के घर हैं।",
    career2:
      "पैसे में मकर सबसे अनुशासित राशि है: फ़िज़ूलख़र्ची नहीं, दीर्घकालीन निवेश और संपत्ति पर भरोसा। ध्यान बस इतना कि कंजूसी और समझदारी की रेखा धुंधली न हो जाए।",
    love1:
      "मकर का प्रेम धीमी आँच पर पकता है: शुरुआत में औपचारिक, पर जो वादा कर दिया वह पत्थर की लकीर। यह राशि फूल और शायरी से कम, ज़िम्मेदारी निभाकर प्रेम जताती है।",
    love2:
      "विवाह में स्थिरता मकर की देन है, और काम-काज में डूबे रहना सबसे बड़ी शिकायत। शनि की राशि होने से कुंडली में शनि की दशा-स्थिति और साढ़े साती का समय मकर वालों के लिए ख़ास देखने लायक़ है।",
    loveChips: [
      { to: "/kundli-matching", label: "कुंडली मिलान" },
      { to: "/sade-sati-calculator", label: "साढ़े साती कैलकुलेटर" },
    ],
    health1:
      "मकर घुटनों, हड्डियों और जोड़ों का स्थान है: शनि की राशि होने से ठंड, जोड़ और दाँत परंपरा में इस राशि के देखने के विषय माने जाते हैं। आराम टालना मकर की आदत है, और यही सबसे पहले सेहत पर दिखता है।",
    health2:
      "सूत्र है धूप: सुबह की धूप, कैल्शियम वाला खाना, और काम के बीच उठकर चलना। छुट्टी काम से भागना नहीं, काम की ही तैयारी है, यह बात मकर को ख़ुद से कहते रहना पड़ता है।",
    gemPara:
      "परंपरा में मकर का रत्न नीलम है, जो स्वामी शनि का रत्न माना जाता है। शुभ रंग गहरा नीला और काला, शुभ अंक 8 और शुभ दिन शनिवार है। नीलम के बारे में परंपरा साफ़ कहती है: बिना कुंडली जँचवाए इसे धारण नहीं करते।",
    faqRulerA:
      "मकर का स्वामी शनि है, जो कर्म, अनुशासन और न्याय का कारक है। इसीलिए मेहनत और ज़िम्मेदारी इस राशि के मूल गुण हैं, और कुंडली में शनि की स्थिति तथा साढ़े साती का समय मकर वालों के लिए ख़ास मायने रखता है।",
    schemaDescription:
      "Makar rashi (Capricorn) profile in Hindi: ruler Saturn, earth element, nature, career, love, lucky colour, gemstone and nakshatras, with daily horoscope and free kundli.",
  },
  {
    path: "/kumbh-rashifal",
    november: [
      "गुरु सातवें भाव में है: साझेदारी, विवाह और क़रार के लिए साल का बड़ा गोचर, और 12 नवंबर से मंगल भी वहीं: रिश्तों में ऊर्जा और बहस दोनों बढ़ सकती हैं, लहजा नरम रखिए। देवउठनी के बाद का विवाह सीज़न आपके लिए ख़ास है: बात आगे बढ़ानी हो तो कुंडली मिलान से शुरुआत कीजिए।",
      "राहु का आपकी राशि में यह आख़िरी पूरा महीना है, 5 दिसंबर को वह राशि बदलता है। आपका स्वामी शनि दूसरे भाव में वक्री है: पैसों और बोली पर संयम ही साढ़ेसाती के इस दौर की असली दवा है। 16 से सूर्य दसवें भाव में: काम की जगह पहचान मिलती है। 13-14 के बाद रुकी बातचीत खुलती है।",
    ],
    hindi: "कुंभ",
    english: "Aquarius",
    order: "ग्यारहवीं",
    symbol: "घड़ा (कलश)",
    ruler: "शनि",
    rulerShort: "शनि",
    element: "वायु",
    nature: "स्थिर",
    color: "नीला",
    number: "8",
    day: "शनिवार",
    gem: "नीलम",
    bodyPart: "पिंडलियाँ और टख़ने",
    compatGood: ["मिथुन", "तुला", "मेष", "धनु"],
    compatWork: ["वृषभ", "सिंह", "वृश्चिक"],
    naks: [
      ["धनिष्ठा", "मंगल", "अंतिम दो चरण"],
      ["शतभिषा", "राहु", "चारों चरण"],
      ["पूर्वा भाद्रपद", "गुरु", "प्रथम तीन चरण"],
    ],
    nakAnswer:
      "धनिष्ठा के अंतिम दो चरण, शतभिषा पूरा, और पूर्वा भाद्रपद के पहले तीन चरण। जन्म नक्षत्र राशि से ज़्यादा बारीक जानकारी देता है, और मुफ़्त कुंडली में यह अपने आप निकल आता है।",
    sub: "राशि चक्र की ग्यारहवीं राशि, स्वामी शनि, तत्व वायु। कुंभ का स्वभाव, करियर, प्रेम, शुभ रंग, रत्न और नक्षत्र, एक ही पेज पर।",
    lede: [
      "कुंभ शनि की वायु राशि है: यहाँ शनि का अनुशासन विचारों में उतरता है, इसलिए यह राशि समय से आगे सोचती है। नया रास्ता, नई तकनीक, नया समाज, कुंभ वहीं सहज है जहाँ बाक़ी अभी पहुँचे नहीं।",
      "यह पेज कुंभ राशि का स्थायी परिचय है। आज का कुंभ राशिफल रोज़ ताज़ा चाहिए तो नीचे डेली होरोस्कोप का रास्ता है, और अपनी कुंडली से सीधा जवाब चाहिए तो मुफ़्त कुंडली बनाकर पूछ सकते हैं।",
    ],
    nature1:
      "कुंभ वाले भीड़ से अलग सोचते हैं और भीड़ के लिए सोचते हैं: समाज, व्यवस्था और सबकी भलाई इनके प्रिय विषय हैं। दोस्ती इस राशि की पूँजी है, जान-पहचान का दायरा हमेशा बड़ा रहता है।",
    nature2:
      "कमज़ोर पक्ष दूरी है: विचारों में इतने ऊँचे कि पास बैठे आदमी की भावना छूट जाए, और ज़िद में स्थिर राशि का पक्कापन। जो कुंभ दिमाग़ के साथ दिल को भी बोलने देता है, वह सचमुच का सुधारक बन जाता है।",
    career1:
      "कुंभ के लिए वे क्षेत्र बने हैं जहाँ नयापन चाहिए: टेक्नोलॉजी और डेटा, विज्ञान और रिसर्च, सामाजिक कार्य, स्टार्टअप, और बड़े नेटवर्क वाले काम। पुराने ढर्रे पर चलने वाली जगहें इस राशि की प्रतिभा बर्बाद करती हैं।",
    career2:
      "पैसा कुंभ के लिए साधन है, साध्य नहीं: कमाई अच्छी हो सकती है पर ध्यान हमेशा काम और विचार पर रहता है। शनि का अनुशासन बचत में भी दिखे, इसके लिए अपने आप कटने वाला निवेश सबसे सही रहता है।",
    love1:
      "कुंभ का प्रेम दोस्ती से शुरू होता है: पहले दिमाग़ मिलता है, फिर दिल। रिश्ते में इस राशि को अपनी जगह और अपने विचारों की आज़ादी चाहिए, जकड़न इसे दूर कर देती है।",
    love2:
      "विवाह में भावना जताने की कमी सबसे आम शिकायत है: प्रेम भीतर पूरा है, बाहर दिखता कम है। कुंडली मिलान के साथ शनि और शुक्र की स्थिति देखना कुंभ वालों के लिए उपयोगी है।",
    loveChips: [
      { to: "/kundli-matching", label: "कुंडली मिलान" },
      { to: "/love-calculator", label: "लव कैलकुलेटर" },
    ],
    health1:
      "कुंभ पिंडलियों और टख़नों का स्थान है, और रक्त संचार इसका विषय: बैठे-बैठे काम करने वाली इस राशि को पैरों की शिकायतें सबसे पहले मिलती हैं।",
    health2:
      "सूत्र है हरकत: हर घंटे उठना, पैदल चलना, और स्क्रीन से नियमित छुट्टी। विचारों में डूबा कुंभ शरीर को भूल जाता है, इसलिए शरीर को याद दिलाने के नियम बनाने पड़ते हैं।",
    gemPara:
      "परंपरा में कुंभ का रत्न नीलम है, जो स्वामी शनि का रत्न माना जाता है। शुभ रंग नीला, शुभ अंक 8 और शुभ दिन शनिवार है। नीलम के बारे में परंपरा साफ़ कहती है: बिना कुंडली जँचवाए इसे धारण नहीं करते।",
    faqRulerA:
      "कुंभ का स्वामी शनि है, पर यहाँ शनि वायु तत्व में है, इसलिए अनुशासन विचारों और समाज की दिशा में काम करता है। कुंडली में शनि की स्थिति और साढ़े साती का समय कुंभ वालों के लिए भी ख़ास मायने रखता है।",
    schemaDescription:
      "Kumbh rashi (Aquarius) profile in Hindi: ruler Saturn, air element, nature, career, love, lucky colour, gemstone and nakshatras, with daily horoscope and free kundli.",
  },
  {
    path: "/meen-rashifal",
    november: [
      "शनि आपकी ही राशि में वक्री है, साढ़ेसाती का बीच का दौर: थकान या बोझ लगे तो उसे संकेत मानिए, ज़िम्मेदारी नहीं, व्यवस्था घटाइए। गुरु छठे भाव में है और 12 नवंबर से मंगल भी: रोग, ऋण और विरोधी, तीनों से निपटने की ताक़त देता है, रूटीन सुधारने और क़र्ज़ चुकाने के लिए महीना अच्छा है।",
      "महीने की शुरुआत आठवें भाव के सूर्य से होती है: साझा पैसे का हिसाब साफ़ रखिए। 16 नवंबर से सूर्य नौवें भाव में आता है: भाग्य का साथ और यात्रा के योग बेहतर। 13-14 के बाद रिश्ते की उलझी बात सुलझती है, और 22 से शुक्र आठवें भाव में जाता है: साझा पैसे के फ़ैसले काग़ज़ पर ही कीजिए।",
    ],
    hindi: "मीन",
    english: "Pisces",
    order: "बारहवीं",
    symbol: "मछलियों का जोड़ा",
    ruler: "गुरु (बृहस्पति)",
    rulerShort: "गुरु",
    element: "जल",
    nature: "द्विस्वभाव",
    color: "पीला",
    number: "3",
    day: "गुरुवार",
    gem: "पुखराज",
    bodyPart: "पैरों के तलवे",
    compatGood: ["कर्क", "वृश्चिक", "वृषभ", "मकर"],
    compatWork: ["मिथुन", "कन्या", "धनु"],
    naks: [
      ["पूर्वा भाद्रपद", "गुरु", "चतुर्थ चरण"],
      ["उत्तरा भाद्रपद", "शनि", "चारों चरण"],
      ["रेवती", "बुध", "चारों चरण"],
    ],
    nakAnswer:
      "पूर्वा भाद्रपद का चौथा चरण, उत्तरा भाद्रपद पूरा, और रेवती पूरा। रेवती राशि चक्र का अंतिम नक्षत्र है। जन्म नक्षत्र राशि से ज़्यादा बारीक जानकारी देता है, और मुफ़्त कुंडली में यह अपने आप निकल आता है।",
    sub: "राशि चक्र की बारहवीं राशि, स्वामी गुरु, तत्व जल। मीन का स्वभाव, करियर, प्रेम, शुभ रंग, रत्न और नक्षत्र, एक ही पेज पर।",
    lede: [
      "मीन राशि चक्र की अंतिम राशि है, और कहा जाता है कि इसमें बाक़ी ग्यारह राशियों का थोड़ा-थोड़ा रंग घुला है। गुरु की जल राशि होने से करुणा, कल्पना और आध्यात्म इसकी पहचान हैं।",
      "यह पेज मीन राशि का स्थायी परिचय है। आज का मीन राशिफल रोज़ ताज़ा चाहिए तो नीचे डेली होरोस्कोप का रास्ता है, और अपनी कुंडली से सीधा जवाब चाहिए तो मुफ़्त कुंडली बनाकर पूछ सकते हैं।",
    ],
    nature1:
      "मीन वाले दूसरों का दर्द अपना मान लेते हैं: करुणा इस राशि में सबसे गहरी है। कल्पनाशक्ति ग़ज़ब की होती है, इसलिए कला, संगीत और आध्यात्म की ओर झुकाव क़ुदरती है। माहौल का असर इन पर जल्दी होता है, अच्छा भी, बुरा भी।",
    nature2:
      "कमज़ोर पक्ष पलायन है: कठोर हक़ीक़त से सपनों में भाग जाना, और 'न' न कह पाना। जो मीन अपनी सीमाएँ खींचना सीख लेता है, उसकी संवेदनशीलता कमज़ोरी नहीं, सबसे बड़ी ताक़त बन जाती है।",
    career1:
      "मीन के लिए वे क्षेत्र बने हैं जहाँ भावना और कल्पना काम आती है: कला, संगीत और लेखन, चिकित्सा और सेवा, मनोविज्ञान और काउंसलिंग, आध्यात्म, और समुद्र या जल से जुड़े काम। कठोर प्रतिस्पर्धा वाले माहौल में इनकी प्रतिभा मुरझाती है।",
    career2:
      "पैसे में मीन का हाथ खुला है, हिसाब कच्चा: मदद करने में आगे, अपने लिए बचाने में पीछे। आमदनी का एक तय हिस्सा अपने आप निवेश में जाए, यह इंतज़ाम मीन के लिए किसी और से ज़्यादा ज़रूरी है।",
    love1:
      "मीन का प्रेम समर्पण है: यह राशि साथी में पूरी तरह घुल जाती है, कभी-कभी ख़ुद को भूलकर। रोमांस की कल्पना सिनेमा जैसी होती है, और छोटी-सी कठोरता भी गहरी चोट करती है।",
    love2:
      "विवाह में मीन निभाने वाला, माफ़ करने वाला पक्ष है, पर अपनी ज़रूरतें कहना इसे सीखना पड़ता है। कुंडली मिलान के साथ गुरु और चंद्रमा की स्थिति देखना मीन वालों के लिए ख़ास उपयोगी है।",
    loveChips: [
      { to: "/kundli-matching", label: "कुंडली मिलान" },
      { to: "/love-calculator", label: "लव कैलकुलेटर" },
    ],
    health1:
      "मीन पैरों के तलवों का स्थान है, और जल तत्व की अंतिम राशि होने से नींद, थकान और रोग-प्रतिरोधक क्षमता इसके देखने के विषय माने जाते हैं। माहौल का असर मीन की सेहत पर भी उतना ही सीधा है जितना मन पर।",
    health2:
      "सूत्र है नींद: सोने का तय समय मीन के लिए किसी दवा से कम नहीं। पानी के पास समय, हल्का व्यायाम और संगीत, यह तिकड़ी इस राशि को सबसे जल्दी ठीक करती है।",
    gemPara:
      "परंपरा में मीन का रत्न पुखराज है, जो स्वामी गुरु का रत्न माना जाता है। शुभ रंग पीला, शुभ अंक 3 और शुभ दिन गुरुवार है: गुरु की दोनों राशियों, धनु और मीन, का रत्न एक ही है।",
    faqRulerA:
      "मीन का स्वामी गुरु (बृहस्पति) है, और जल तत्व में गुरु का ज्ञान करुणा और आध्यात्म का रूप लेता है। कुंडली में गुरु और चंद्रमा की स्थिति मीन वालों के मन और भाग्य के बारे में बहुत कुछ बताती है।",
    schemaDescription:
      "Meen rashi (Pisces) profile in Hindi: ruler Jupiter, water element, nature, career, love, lucky colour, gemstone and nakshatras, with daily horoscope and free kundli.",
  },
];

function otherSigns(exceptPath: string): { to: string; label: string }[] {
  return SIGN_DATA.filter((s) => s.path !== exceptPath).map((s) => ({
    to: s.path,
    label: `${s.hindi} राशिफल`,
  }));
}

/* The daily-CTA section is the same honest message on every page; the
   profile sections around it are sign-specific. */
function dailySection(signHindi: string): LandingSection {
  return {
    h2: `आज का ${signHindi} राशिफल कैसे देखें`,
    body: [
      `इस पेज पर ऊपर का मासिक राशिफल हर महीने ताज़ा होता है, पर रोज़ का राशिफल यहाँ नहीं लिखा जाता: जो पेज रोज़ बदलता नहीं, उस पर "आज" लिखना पाठक को धोखा देना है। आज का राशिफल रोज़ ताज़ा बनता है और डेली होरोस्कोप पेज पर मिलता है।`,
      `और अगर जवाब सिर्फ़ राशि से नहीं, अपनी पूरी जन्म कुंडली से चाहिए, तो मुफ़्त कुंडली बनाकर AI ज्योतिषी से हिंदी में पूछिए। पहला सवाल मुफ़्त है।`,
    ],
    chips: [
      { to: "/daily-horoscope", label: "आज का राशिफल" },
      { to: "/free-kundli", label: "मुफ़्त कुंडली" },
      { to: "/ai-jyotish", label: "AI ज्योतिष चैट" },
      { to: "/hi", label: "हिंदी में ज्योतिष" },
    ],
  };
}

function buildSign(s: SignData): LandingConfig {
  const why = COMPAT_WHY[s.element];
  return {
    url: `https://astroaura.ai${s.path}`,
    title: `${s.hindi} राशिफल: स्वभाव, करियर, प्रेम, शुभ रंग और रत्न | Aura AI`,
    description: `${s.hindi} राशि का पूरा परिचय हिंदी में: स्वामी ग्रह ${s.rulerShort}, ${s.element} तत्व, स्वभाव, करियर, प्रेम, शुभ रंग, शुभ अंक, रत्न और नक्षत्र। नवंबर 2026 का मासिक राशिफल, आज का राशिफल और मुफ़्त कुंडली भी।`,
    lang: "hi",
    wide: true,
    eyebrow: "राशिफल",
    h1Pre: s.hindi,
    h1Accent: "राशिफल",
    sub: s.sub,
    primaryCta: "पहला सवाल मुफ़्त पूछिए",
    secondaryCta: "मुफ़्त कुंडली बनाइए",
    schemaName: `${s.english} Rashifal (${s.hindi}) - Aura AI`,
    schemaDescription: s.schemaDescription,
    areaServed: ["IN"],
    priceCurrency: "INR",
    profile: [
      { label: "राशि चक्र में स्थान", value: `${s.order} राशि` },
      { label: "चिह्न", value: s.symbol },
      { label: "स्वामी ग्रह", value: s.ruler },
      { label: "तत्व", value: s.element },
      { label: "प्रकृति", value: s.nature },
      { label: "शुभ रंग", value: s.color },
      { label: "शुभ अंक", value: s.number },
      { label: "शुभ दिन", value: s.day },
      { label: "शुभ रत्न", value: s.gem },
      { label: "शरीर में स्थान", value: s.bodyPart },
    ],
    lede: s.lede,
    sections: [
      {
        h2: `${s.hindi} राशिफल नवंबर 2026`,
        body: [NOV_CONTEXT, ...s.november],
        chips: [
          { to: "/blog/november-2026-rashifal", label: "नवंबर 2026: सभी 12 राशियाँ" },
          { to: "/blog/diwali-2026-rashifal", label: "दिवाली 2026 राशिफल" },
          { to: "/muhurat", label: "शुभ मुहूर्त" },
          { to: "/free-kundli", label: "मुफ़्त कुंडली" },
        ],
      },
      {
        h2: `${s.hindi} का स्वभाव`,
        body: [s.nature1, s.nature2],
      },
      {
        h2: "करियर और पैसा",
        body: [s.career1, s.career2],
      },
      {
        h2: "प्रेम और विवाह",
        body: [s.love1, s.love2],
        chips: s.loveChips,
      },
      {
        h2: `${s.hindi} का तालमेल किन राशियों से`,
        body: [
          "यह सूची तत्वों और स्वामी ग्रहों की मित्रता से बनी सामान्य तस्वीर है। असली जवाब दोनों की पूरी कुंडली के मिलान से आता है: 36 गुण, नाड़ी-भकूट और मंगल दोष की जाँच के बाद। इसलिए सूची को शुरुआत मानिए, फ़ैसला नहीं।",
        ],
        cards: [
          {
            label: "सहज तालमेल",
            title: s.compatGood.join(", "),
            body: why.good,
            tone: "good",
          },
          {
            label: "मेहनत माँगता तालमेल",
            title: s.compatWork.join(", "),
            body: why.work,
            tone: "ok",
          },
        ],
        chips: [{ to: "/kundli-matching", label: "36 गुण कुंडली मिलान" }],
      },
      {
        h2: "स्वास्थ्य और सावधानी",
        body: [s.health1, s.health2],
      },
      {
        h2: `${s.hindi} राशि का शुभ रत्न, रंग और दिन`,
        body: [s.gemPara, GEM_CAUTION],
        chips: [{ to: "/ai-jyotish", label: "AI ज्योतिष चैट" }],
      },
      {
        h2: `${s.hindi} राशि के नक्षत्र`,
        body: [
          `जन्म के समय चंद्रमा ${s.hindi} राशि के जिस हिस्से में था, उसी से जन्म नक्षत्र तय होता है। राशि एक मोटा नक़्शा है, नक्षत्र उसी का बारीक पता: इसीलिए एक ही राशि के दो लोगों का स्वभाव नक्षत्र बदलते ही अलग दिखता है। अपना नक्षत्र मुफ़्त कुंडली में अपने आप निकल आता है।`,
        ],
        table: {
          head: ["नक्षत्र", "स्वामी ग्रह", "चरण"],
          rows: s.naks.map((n) => [n[0], n[1], n[2]]),
          caption: `${s.hindi} राशि में आने वाले नक्षत्र और उनके स्वामी ग्रह`,
        },
      },
      dailySection(s.hindi),
      {
        h2: "बाक़ी राशियों का राशिफल",
        chips: otherSigns(s.path),
      },
    ],
    faqs: [
      {
        q: `${s.hindi} राशि का स्वामी ग्रह कौन है?`,
        a: s.faqRulerA,
      },
      {
        q: `${s.hindi} राशि का शुभ रंग और शुभ अंक क्या है?`,
        a: `${s.hindi} राशि का शुभ रंग ${s.color} और शुभ अंक ${s.number} माना जाता है, शुभ दिन ${s.day} है। ये परंपरागत मान्यताएँ हैं: अपनी कुंडली के हिसाब से सटीक जवाब चाहिए तो मुफ़्त कुंडली बनाकर AI ज्योतिषी से पूछ सकते हैं।`,
      },
      {
        q: `${s.hindi} राशि वालों को कौन सा रत्न पहनना चाहिए?`,
        a: `परंपरा में ${s.hindi} राशि का रत्न ${s.gem} है, जो स्वामी ग्रह ${s.rulerShort} का रत्न है। पर रत्न सिर्फ़ राशि देखकर नहीं पहना जाता: कुंडली में ${s.rulerShort} की स्थिति देखकर ही तय होता है कि रत्न सूट करेगा या नहीं।`,
      },
      {
        q: `${s.hindi} राशि की शादी किस राशि से अच्छी रहती है?`,
        a: `तत्व और ग्रह-मैत्री के हिसाब से ${s.hindi} का सहज तालमेल ${s.compatGood.join(", ")} राशियों से माना जाता है। पर शादी का असली जवाब दो राशियों से नहीं, दोनों कुंडलियों के मिलान से आता है: 36 गुण, नाड़ी-भकूट और मंगल दोष की जाँच के बाद।`,
      },
      {
        q: `${s.hindi} राशि में कौन से नक्षत्र आते हैं?`,
        a: s.nakAnswer,
      },
      {
        q: `आज का ${s.hindi} राशिफल कहाँ देखें?`,
        a: "डेली होरोस्कोप पेज पर हर दिन का राशिफल ताज़ा मिलता है। व्यक्तिगत जवाब चाहिए तो मुफ़्त कुंडली बनाकर AI ज्योतिषी से हिंदी में पूछिए, पहला सवाल मुफ़्त है।",
      },
    ],
    finalHeading: "सिर्फ़ राशि नहीं, अपनी कुंडली से पूछिए",
    finalText:
      "राशिफल बारह हिस्सों में बँटा जवाब है। आपकी जन्म कुंडली सिर्फ़ आपकी है। मुफ़्त कुंडली बनाइए और पहला सवाल हिंदी में पूछिए।",
    relatedLinks: [{ to: "/hi", label: "हिंदी में ज्योतिष" }],
    disclaimer: DISCLAIMER,
  };
}

export const rashifalPages: LandingConfig[] = SIGN_DATA.map(buildSign);

/** The 12 paths, for the route map in App.tsx and anywhere the list is
    needed without importing every config's content. */
export const rashifalPaths: string[] = SIGN_DATA.map((s) => s.path);
